// src/screens/CarHistory.js v1.9
import React, { useState, useEffect } from 'react';
import { getVehicles, getSettings } from '../database/db';
import { getPrinterDevice, printExitReceipt } from '../utils/bluetooth-printer';
import { format } from 'date-fns';
import { Search, Printer, X } from 'lucide-react';
import QRCode from 'qrcode';

const fmtDT = iso => { try { return format(new Date(iso),'dd/MM HH:mm'); } catch { return '—'; } };
const fmtDate = iso => { try { return format(new Date(iso),'dd/MM/yyyy'); } catch { return '—'; } };
const fmtTime = iso => { try { return format(new Date(iso),'hh:mm a'); } catch { return '—'; } };
const fmt = n => `AED ${Number(n||0).toFixed(2)}`;
const getLabel = pt => ({'monthly-24hr':'Monthly 24hr','monthly-night':'Monthly Night','monthly-day':'Monthly Day','weekly':'Weekly','biweekly':'Bi-Weekly','hourly':'Hourly','overnight':'Overnight','mou':'MOU'}[pt]||pt||'Hourly');

const FILTERS = ['all','hourly','overnight','monthly-24hr','monthly-night','monthly-day','weekly','biweekly','mou'];

export default function CarHistory() {
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [printVehicle, setPrintVehicle] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [btPrinting, setBtPrinting] = useState(false);
  const [btAlert, setBtAlert] = useState('');
  const [qrData, setQrData] = useState('');
  const settings = getSettings();

  useEffect(() => { setVehicles(getVehicles()); }, []);

  // Date filter logic
  const getDateRange = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const yesterday = new Date(now-86400000).toISOString().split('T')[0];
    const week = new Date(now-7*86400000).toISOString().split('T')[0];
    const monthStart = new Date(now.getFullYear(),now.getMonth(),1).toISOString().split('T')[0];
    if(dateFilter==='today') return {from:today,to:today};
    if(dateFilter==='yesterday') return {from:yesterday,to:yesterday};
    if(dateFilter==='week') return {from:week,to:today};
    if(dateFilter==='month') return {from:monthStart,to:today};
    if(dateFilter==='custom') return {from:customFrom,to:customTo};
    return {from:'',to:''};
  };

  const btPrint = async(v) => {
    const device = getPrinterDevice();
    if(!device){setBtAlert('Printer not connected. Go to Settings → Printer.');setTimeout(()=>setBtAlert(''),4000);return;}
    setBtPrinting(true);
    try{
      const {autoReconnectAndPrint} = await import('../utils/bluetooth-printer');
      await autoReconnectAndPrint('exit',v,settings);setBtAlert('✅ Printed!');}
    catch(e){setBtAlert('❌ '+e.message);}
    setBtPrinting(false);
    setTimeout(()=>setBtAlert(''),4000);
  };

  const filtered = vehicles
    .filter(v => v.status !== 'parked')
    .filter(v => {
      if(search.length>=2 && !v.plate?.toLowerCase().includes(search.toLowerCase())) return false;
      if(filter!=='all' && v.packageType!==filter) return false;
      // Date filter
      const range = getDateRange();
      if(range.from && v.entryTime) {
        const entryDate = v.entryTime.split('T')[0];
        if(entryDate < range.from) return false;
        if(range.to && entryDate > range.to) return false;
      }
      return true;
    })
    .sort((a,b) => new Date(b.entryTime||0) - new Date(a.entryTime||0));

  const openPrint = async(v) => {
    const qr = await QRCode.toDataURL(JSON.stringify({plate:v.plate,entry:v.entryTime,exit:v.exitTime,fee:v.fee})).catch(()=>'');
    setQrData(qr);
    setPrintVehicle(v);
  };

  const doPrint = () => window.print();

  // Group counts
  const hourlyCount = vehicles.filter(v=>v.status!=='parked'&&(!v.packageType||v.packageType==='hourly')).length;
  const othersCount = vehicles.filter(v=>v.status!=='parked'&&v.packageType&&v.packageType!=='hourly').length;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">🚗 Car History</div><div className="page-subtitle">{vehicles.filter(v=>v.status!=='parked').length} total · {hourlyCount} hourly · {othersCount} packages</div></div>
      </div>
      <div className="page-body">

        {btAlert&&<div className={`alert ${btAlert.startsWith('✅')?'alert-success':'alert-danger'}`} style={{marginBottom:8}}>{btAlert}</div>}
        {/* Search */}
        <div style={{position:'relative',marginBottom:12}}>
          <Search size={15} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}/>
          <input className="form-input plate-input" style={{paddingLeft:40,paddingRight:search?48:16,fontSize:18,textAlign:'left'}}
            placeholder="Type plate to search..." value={search} onChange={e=>setSearch(e.target.value.toUpperCase())}/>
          {search&&<button onClick={()=>setSearch('')} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'var(--danger)',border:'none',cursor:'pointer',color:'white',padding:'4px 8px',borderRadius:6,fontSize:13,fontWeight:700}}>✕</button>}
        </div>

        {/* Date filter */}
        <div style={{overflowX:'auto',marginBottom:10}}>
          <div style={{display:'flex',gap:6,minWidth:'max-content'}}>
            {[['all','All Time'],['today','Today'],['yesterday','Yesterday'],['week','Last 7 Days'],['month','This Month'],['custom','Custom']].map(([k,l])=>(
              <button key={k} onClick={()=>setDateFilter(k)} className={`btn btn-sm ${dateFilter===k?'btn-primary':'btn-outline'}`}>{l}</button>
            ))}
          </div>
        </div>
        {dateFilter==='custom'&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
            <div className="form-group" style={{margin:0}}><label className="form-label">From</label><input className="form-input" type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)}/></div>
            <div className="form-group" style={{margin:0}}><label className="form-label">To</label><input className="form-input" type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)}/></div>
          </div>
        )}
        {/* Package filter tabs */}
        <div style={{overflowX:'auto',marginBottom:14}}>
          <div style={{display:'flex',gap:6,minWidth:'max-content'}}>
            {[['all','All'],['hourly','Hourly'],['overnight','Overnight'],['monthly-24hr','Monthly 24hr'],['monthly-night','Monthly Night'],['monthly-day','Monthly Day'],['weekly','Weekly'],['biweekly','Bi-Weekly'],['mou','MOU']].map(([k,l])=>(
              <button key={k} onClick={()=>setFilter(k)} className={`btn btn-sm ${filter===k?'btn-primary':'btn-outline'}`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:14}}>
          {[
            ['Total Visits',vehicles.filter(v=>v.status!=='parked').length,'var(--accent)'],
            ['Hourly',hourlyCount,'var(--accent2)'],
            ['Packages',othersCount,'var(--success)'],
          ].map(([l,n,c])=>(
            <div key={l} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'12px',textAlign:'center'}}>
              <div style={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:800,color:c}}>{n}</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4,fontFamily:'var(--font-mono)',textTransform:'uppercase'}}>{l}</div>
            </div>
          ))}
        </div>

        {/* History list */}
        {filtered.length===0
          ?<div className="empty-state"><div className="empty-state-icon">🚗</div><div className="empty-state-text">No records found</div></div>
          :<div style={{display:'flex',flexDirection:'column',gap:8}}>
            {filtered.slice(0,100).map(v=>(
              <div key={v.id} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'12px 14px'}}>
                <div className="flex-between" style={{marginBottom:8}}>
                  <div style={{fontFamily:'var(--font-mono)',fontSize:18,fontWeight:700,color:'var(--accent)'}}>{v.plate}</div>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <span className="badge badge-neutral" style={{fontSize:10}}>{getLabel(v.packageType)}</span>
                    {v.status!=='parked'&&(
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-outline btn-sm" onClick={()=>openPrint(v)} style={{gap:4}}>
                          <Printer size={13}/> View
                        </button>
                        <button className="btn btn-primary btn-sm" onClick={()=>btPrint(v)} disabled={btPrinting} style={{gap:4,fontSize:12}}>
                          🖨️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,fontSize:13}}>
                  <div><span style={{color:'var(--text-muted)'}}>Entry: </span><span style={{fontFamily:'var(--font-mono)'}}>{fmtDT(v.entryTime)}</span></div>
                  <div><span style={{color:'var(--text-muted)'}}>Exit: </span><span style={{fontFamily:'var(--font-mono)'}}>{v.exitTime?fmtDT(v.exitTime):<span className="badge badge-warning">Parked</span>}</span></div>
                  <div><span style={{color:'var(--text-muted)'}}>Fee: </span><span style={{fontFamily:'var(--font-mono)',color:'var(--success)',fontWeight:600}}>{fmt(v.fee)}</span></div>
                  <div><span style={{color:'var(--text-muted)'}}>Method: </span><span style={{textTransform:'uppercase',fontSize:12}}>{v.paymentMethod||'—'}</span></div>
                  {v.staffName&&<div style={{gridColumn:'1/-1'}}><span style={{color:'var(--text-muted)'}}>Staff: </span><span>{v.staffName}</span></div>}
                </div>
              </div>
            ))}
            {filtered.length>100&&<div style={{textAlign:'center',fontSize:13,color:'var(--text-muted)',padding:8}}>Showing 100 of {filtered.length}. Use search to find specific plates.</div>}
          </div>
        }
      </div>

      {/* PRINT RECEIPT MODAL */}
      {printVehicle&&(
        <div className="modal-overlay" onClick={()=>setPrintVehicle(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🖨️ Exit Receipt — {printVehicle.plate}</div>
              <div className="flex-row">
                <button className="btn btn-primary btn-sm" onClick={doPrint}><Printer size={13}/> Print</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPrintVehicle(null)}><X size={14}/></button>
              </div>
            </div>
            <div className="modal-body">
              <div className="receipt">
                <div className="receipt-header">
                  <div className="receipt-title">AL BARAKAH CAR PARKING</div>
                  <div style={{fontSize:10,color:'#555'}}>{settings.address}</div>
                  <div style={{fontSize:10,fontWeight:700,marginTop:2}}>— EXIT RECEIPT —</div>
                </div>
                <hr className="receipt-divider"/>
                <div className="receipt-row"><span>Date:</span><span>{fmtDate(printVehicle.exitTime||printVehicle.entryTime)}</span></div>
                <div className="receipt-row"><span>Entry:</span><span>{fmtTime(printVehicle.entryTime)}</span></div>
                <div className="receipt-row"><span>Exit:</span><span>{fmtTime(printVehicle.exitTime)}</span></div>
                <div className="receipt-row"><span>Duration:</span><span>{printVehicle.duration||'—'}</span></div>
                <div className="receipt-row"><span>Plate No.:</span><span style={{fontWeight:700}}>{printVehicle.plate}</span></div>
                <div className="receipt-row"><span>Package:</span><span>{getLabel(printVehicle.packageType)}</span></div>
                <div className="receipt-row"><span>Car Type:</span><span>{printVehicle.carType==='open'?'◯ Open':'● Closed'}</span></div>
                <hr className="receipt-divider"/>
                {printVehicle.discountAmount>0&&<div className="receipt-row"><span>Discount:</span><span>- {fmt(printVehicle.discountAmount)}</span></div>}
                {printVehicle.overnightAdvance>0&&<div className="receipt-row"><span>Advance Paid:</span><span style={{color:'#060',fontWeight:700}}>{fmt(printVehicle.overnightAdvance)}</span></div>}
                <div className="receipt-row receipt-total"><span>TOTAL PAID:</span><span>{fmt(printVehicle.fee)}</span></div>
                <div className="receipt-row"><span>Payment:</span><span style={{textTransform:'uppercase'}}>{printVehicle.paymentMethod||'—'}</span></div>
                <div className="receipt-row"><span>Staff:</span><span>{printVehicle.exitStaffName||printVehicle.staffName||'—'}</span></div>
                {qrData&&<div style={{textAlign:'center',marginTop:10}}><img src={qrData} alt="QR" style={{width:65,height:65}}/><div style={{fontSize:9,color:'#999',marginTop:2}}>Scan to verify</div></div>}
                <hr className="receipt-divider"/>
                <div style={{textAlign:'center',fontSize:9,color:'#666'}}>{settings.receiptFooter}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
